import { DOCUMENT, inject, Injectable, signal, WritableSignal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ScriptLoaderService {
  private readonly document = inject(DOCUMENT);

  private readonly requestedScripts = new Set<string>();
  private readonly loadedStates = new Map<string, WritableSignal<boolean>>();

  loadScript(src: string): void {
    if (this.requestedScripts.has(src)) {
      return;
    }
    this.requestedScripts.add(src);

    const isLoaded = this.getOrCreateLoadedState(src);

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = true;

    script.onload = (): void => {
      isLoaded.set(true);
    };

    script.onerror = (error: unknown): void => {
      console.error(`Failed to load script: ${src}`, error);
      // Allow a retry on the next loadScript() call
      this.requestedScripts.delete(src);
      script.remove();
    };

    this.document.body.appendChild(script);
  }

  isLoaded(src: string): boolean {
    return this.getOrCreateLoadedState(src)();
  }

  private getOrCreateLoadedState(src: string): WritableSignal<boolean> {
    let state = this.loadedStates.get(src);
    if (!state) {
      state = signal(false);
      this.loadedStates.set(src, state);
    }
    return state;
  }
}
